import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/inherits";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _callSuper(t, o, e) { return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e)); }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
import React from 'react';
import { injectIntl } from 'react-intl-next';
import uuid from 'uuid/v4';
import { ACTION, ACTION_SUBJECT, EVENT_TYPE, fireAnalyticsEvent, FULL_WIDTH_MODE, getAnalyticsEventsFromTransaction, PLATFORMS } from '@atlaskit/editor-common/analytics';
import { browser } from '@atlaskit/editor-common/browser';
import { getDocStructure } from '@atlaskit/editor-common/core-utils';
import { countNodes as _countNodes } from '@atlaskit/editor-common/count-nodes';
import { createDispatch, EventDispatcher } from '@atlaskit/editor-common/event-dispatcher';
import { getEnabledFeatureFlagKeys } from '@atlaskit/editor-common/normalize-feature-flags';
import { startMeasure, stopMeasure } from '@atlaskit/editor-common/performance-measures';
import { measureRender } from '@atlaskit/editor-common/performance/measure-render';
import { getResponseEndTime } from '@atlaskit/editor-common/performance/navigation';
import { EditorPluginInjectionAPI } from '@atlaskit/editor-common/preset';
import { processRawValue, processRawValueWithoutValidation } from '@atlaskit/editor-common/process-raw-value';
import { ReactEditorViewContext } from '@atlaskit/editor-common/ui-react';
import { analyticsEventKey, getAnalyticsEventSeverity } from '@atlaskit/editor-common/utils/analytics';
import { EditorState, Selection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { findChangedNodesFromTransaction } from '../utils/findChangedNodesFromTransaction';
import { getNodesCount } from '../utils/getNodesCount';
import { isFullPage } from '../utils/is-full-page';
import { RenderTracking } from '../utils/performance/components/RenderTracking';
import measurements from '../utils/performance/measure-enum';
import { PluginPerformanceObserver } from '../utils/performance/plugin-performance-observer';
import { freezeUnsafeTransactionProperties } from '../utils/performance/safer-transactions';
import { EVENT_NAME_ON_CHANGE } from '../utils/performance/track-transactions';
import { validateNodes, validNode } from '../utils/validateNodes';
import { PROSEMIRROR_RENDERED_DEGRADED_SEVERITY_THRESHOLD, PROSEMIRROR_RENDERED_NORMAL_SEVERITY_THRESHOLD } from './consts';
import { createErrorReporter, createPMPlugins, processPluginsList } from './create-editor';
import createPluginsList from './create-plugins-list';
import { createSchema } from './create-schema';
import { createFeatureFlagsFromProps } from './feature-flags-from-props';
import { editorMessages } from './messages';
var EDIT_AREA_ID = 'ak-editor-textarea';
function handleEditorFocus(view) {
  if (view.hasFocus()) {
    return;
  }
  return window.setTimeout(function () {
    if (view.hasFocus()) {
      return;
    }
    if (!window.getSelection) {
      view.focus();
      return;
    }
    var domSelection = window.getSelection();
    if (!domSelection || domSelection.rangeCount === 0) {
      view.focus();
      return;
    }
    var range = domSelection.getRangeAt(0);
    // if selection is outside editor focus and exit
    if (range.startContainer.contains(view.dom)) {
      view.focus();
      return;
    }
    // set cursor/selection and focus
    var anchor = view.posAtDOM(range.startContainer, range.startOffset);
    var head = view.posAtDOM(range.endContainer, range.endOffset);
    // if anchor or head < 0 focus and exit
    if (anchor < 0 || head < 0) {
      view.focus();
      return;
    }
    var selection = TextSelection.create(view.state.doc, anchor, head);
    var tr = view.state.tr.setSelection(selection);
    view.dispatch(tr);
    view.focus();
  }, 0);
}
// Ignored via go/ees005
// eslint-disable-next-line @repo/internal/react/no-class-components
export var ReactEditorView = /*#__PURE__*/function (_React$Component) {
  function ReactEditorView(props) {
    var _this$props$editorPro3, _props$setEditorAPI;
    var _this;
    _classCallCheck(this, ReactEditorView);
    _this = _callSuper(this, ReactEditorView, [props]);
    _defineProperty(_this, "editorRef", /*#__PURE__*/React.createRef());
    // ProseMirror is instantiated prior to the initial React render cycle,
    // so we allow transactions by default, to avoid discarding the initial one.
    _defineProperty(_this, "canDispatchTransactions", true);
    _defineProperty(_this, "onPluginObservation", function (report) {
      var _this$pluginInjection;
      _this.dispatchAnalyticsEvent({
        action: ACTION.TRANSACTION_DISPATCHED,
        actionSubject: ACTION_SUBJECT.EDITOR,
        eventType: EVENT_TYPE.OPERATIONAL,
        attributes: {
          report: report,
          participants: ((_this$pluginInjection = _this.pluginInjectionAPI.api().collabEdit) === null || _this$pluginInjection === void 0 || (_this$pluginInjection = _this$pluginInjection.sharedState.currentState()) === null || _this$pluginInjection === void 0 || (_this$pluginInjection = _this$pluginInjection.participants) === null || _this$pluginInjection === void 0 ? void 0 : _this$pluginInjection.size()) || 1
        }
      });
    });
    _defineProperty(_this, "getEditorState", function () {
      var _this$view;
      return (_this$view = _this.view) === null || _this$view === void 0 ? void 0 : _this$view.state;
    });
    _defineProperty(_this, "getEditorView", function () {
      return _this.view;
    });
    _defineProperty(_this, "formatFullWidthAppearance", function (appearance) {
      if (appearance === 'full-width') {
        return FULL_WIDTH_MODE.FULL_WIDTH;
      }
      return FULL_WIDTH_MODE.FIXED_WIDTH;
    });
    _defineProperty(_this, "resetEditorState", function (_ref) {
      var _this$props$editorPro, _this$props$editorPro2;
      var doc = _ref.doc,
        shouldScrollToBottom = _ref.shouldScrollToBottom;
      if (!_this.view) {
        return;
      }

      // We cannot currently guarentee when all the portals will have re-rendered during a reconfigure
      // so we blur here to stop ProseMirror from trying to apply selection to detached nodes or
      // nodes that haven't been re-rendered to the document yet.
      _this.blur();
      _this.featureFlags = createFeatureFlagsFromProps(_this.props.editorProps.featureFlags);
      _this.editorState = _this.createEditorState({
        props: _this.props,
        doc: doc,
        resetting: true,
        selectionAtStart: !shouldScrollToBottom
      });
      _this.view.updateState(_this.editorState);
      (_this$props$editorPro = (_this$props$editorPro2 = _this.props.editorProps).onChange) === null || _this$props$editorPro === void 0 || _this$props$editorPro.call(_this$props$editorPro2, _this.view, {
        source: 'local'
      });
    });
    _defineProperty(_this, "blur", function () {
      if (!_this.view) {
        return;
      }
      if (_this.view.dom instanceof HTMLElement && _this.view.hasFocus()) {
        _this.view.dom.blur();
      }

      // The selectionToDOM method uses the document selection to determine currently selected node
      // We need to mimic blurring this as it seems doing the above is not enough.
      // @ts-expect-error
      var sel = _this.view.root.getSelection();
      if (sel) {
        sel.removeAllRanges();
      }
    });
    _defineProperty(_this, "handleAnalyticsEvent", function (payload) {
      fireAnalyticsEvent(_this.props.createAnalyticsEvent)(payload);
    });
    _defineProperty(_this, "editorPlugins", []);
    _defineProperty(_this, "createEditorState", function (options) {
      var _api$editorViewMode;
      var schema;
      if (_this.view) {
        if (options.resetting) {
          /**
           * ReactEditorView currently does NOT handle dynamic schema,
           * We are reusing the existing schema, and rely on #reconfigureState
           * to update `this.config`
           */
          schema = _this.view.state.schema;
        } else {
          /**
           * There's presently a number of issues with changing the schema of a
           * editor inflight. A significant issue is that we lose the ability
           * to keep track of a user's history as the internal plugin state
           * keeps a list of Steps to undo/redo (which are tied to the schema).
           * Without a good way to do work around this, we prevent this for now.
           */
          // eslint-disable-next-line no-console
          console.warn('The editor does not support changing the schema dynamically.');
          return _this.editorState;
        }
      } else {
        _this.config = processPluginsList(_this.getPlugins(options.props.preset));
        schema = createSchema(_this.config);
      }
      var contentTransformerProvider = options.props.editorProps.contentTransformerProvider;
      var plugins = createPMPlugins({
        schema: schema,
        dispatch: _this.dispatch,
        errorReporter: _this.errorReporter,
        editorConfig: _this.config,
        eventDispatcher: _this.eventDispatcher,
        providerFactory: options.props.providerFactory,
        portalProviderAPI: _this.props.portalProviderAPI,
        nodeViewPortalProviderAPI: _this.props.nodeViewPortalProviderAPI,
        dispatchAnalyticsEvent: _this.dispatchAnalyticsEvent,
        featureFlags: _this.featureFlags,
        getIntl: function getIntl() {
          return _this.props.intl;
        },
        onEditorStateUpdated: fg('platform_editor_catch_missing_injection_states') ? _this.pluginInjectionAPI.onEditorViewUpdated : undefined
      });
      _this.contentTransformer = contentTransformerProvider ? contentTransformerProvider(schema) : undefined;
      var doc;
      var api = _this.pluginInjectionAPI.api();
      // If we have a doc prop, we need to process it into a PMNode

      if (options.doc) {
        // if the collabEdit API is set, skip this validation due to potential pm validation errors
        // from docs that end up with invalid marks after processing (See #hot-111702 for more details)

        if ((api === null || api === void 0 ? void 0 : api.collabEdit) !== undefined && fg('editor_load_conf_collab_docs_without_checks') || options.props.editorProps.skipValidation) {
          doc = processRawValueWithoutValidation(schema, options.doc, _this.dispatchAnalyticsEvent);
        } else {
          doc = processRawValue(schema, options.doc, options.props.providerFactory, options.props.editorProps.sanitizePrivateContent, _this.contentTransformer, _this.dispatchAnalyticsEvent);
        }
      }
      var isViewMode = (api === null || api === void 0 || (_api$editorViewMode = api.editorViewMode) === null || _api$editorViewMode === void 0 ? void 0 : _api$editorViewMode.sharedState.currentState().mode) === 'view';
      var selection;
      if (doc) {
        if (isViewMode) {
          var emptySelection = new TextSelection(doc.resolve(0));
          return EditorState.create({
            schema: schema,
            plugins: plugins,
            doc: doc,
            selection: emptySelection
          });
        } else {
          selection = options.selectionAtStart ? Selection.atStart(doc) : Selection.atEnd(doc);
        }
      }
      // Workaround for ED-3507: When media node is the last element, scrollIntoView throws an error
      var patchedSelection = selection ? Selection.findFrom(selection.$head, -1, true) || undefined : undefined;
      return EditorState.create({
        schema: schema,
        plugins: plugins,
        doc: doc,
        selection: patchedSelection
      });
    });
    _defineProperty(_this, "onEditorViewStateUpdated", function (_ref2) {
      var originalTransaction = _ref2.originalTransaction,
        transactions = _ref2.transactions,
        oldEditorState = _ref2.oldEditorState,
        newEditorState = _ref2.newEditorState;
      _this.config.onEditorViewStateUpdatedCallbacks.forEach(function (entry) {
        entry.callback({
          originalTransaction: originalTransaction,
          transactions: transactions,
          oldEditorState: oldEditorState,
          newEditorState: newEditorState
        });
      });
    });
    _defineProperty(_this, "dispatchTransaction", function (unsafeTransaction) {
      if (!_this.view) {
        return;
      }
      var nodes = findChangedNodesFromTransaction(unsafeTransaction);
      var changedNodesValid = validateNodes(nodes);
      var transaction = new Proxy(unsafeTransaction, freezeUnsafeTransactionProperties({
        dispatchAnalyticsEvent: _this.dispatchAnalyticsEvent,
        pluginKey: 'unknown-reacteditorview'
      }));
      if (changedNodesValid) {
        var oldEditorState = _this.view.state;

        // go ahead and update the state now we know the transaction is good
        var _this$view$state$appl = _this.view.state.applyTransaction(transaction),
          editorState = _this$view$state$appl.state,
          transactions = _this$view$state$appl.transactions;
        if (editorState === oldEditorState) {
          return;
        }
        _this.view.updateState(editorState);
        if (!fg('platform_editor_catch_missing_injection_states')) {
          _this.pluginInjectionAPI.onEditorViewUpdated({
            newEditorState: editorState,
            oldEditorState: oldEditorState
          });
        }

        // ED-25839: Investigate if we also want to migrate this API to use `onEditorStateUpdated` in `createPMPlugins`
        _this.onEditorViewStateUpdated({
          originalTransaction: transaction,
          transactions: transactions,
          oldEditorState: oldEditorState,
          newEditorState: editorState
        });
        if (_this.props.editorProps.onChange && transaction.docChanged) {
          var source = transaction.getMeta('isRemote') ? 'remote' : 'local';
          startMeasure(EVENT_NAME_ON_CHANGE);
          _this.props.editorProps.onChange(_this.view, {
            source: source
          });
          stopMeasure(EVENT_NAME_ON_CHANGE, function (duration, startTime) {
            _this.dispatchAnalyticsEvent({
              action: ACTION.ON_CHANGE_CALLBACK,
              actionSubject: ACTION_SUBJECT.EDITOR,
              eventType: EVENT_TYPE.OPERATIONAL,
              attributes: {
                duration: duration,
                startTime: startTime
              }
            });
          });
        }
        _this.editorState = editorState;
      } else {
        var invalidNodes = nodes.filter(function (node) {
          return !validNode(node);
        }).map(function (node) {
          return getDocStructure(node, {
            compact: true
          });
        });
        _this.dispatchAnalyticsEvent({
          action: ACTION.DISPATCHED_INVALID_TRANSACTION,
          actionSubject: ACTION_SUBJECT.EDITOR,
          eventType: EVENT_TYPE.OPERATIONAL,
          attributes: {
            analyticsEventPayloads: getAnalyticsEventsFromTransaction(transaction),
            invalidNodes: invalidNodes
          }
        });
      }
    });
    _defineProperty(_this, "getDirectEditorProps", function (state) {
      return {
        state: state || _this.editorState,
        dispatchTransaction: function dispatchTransaction(tr) {
          // Block stale transactions:
          // Prevent runtime exeptions from async transactions that would attempt to
          // update the DOM after React has unmounted the Editor.
          if (_this.canDispatchTransactions) {
            _this.dispatchTransaction(tr);
          }
        },
        // Disables the contentEditable attribute of the editor if the editor is disabled
        editable: function editable(_state) {
          return !_this.props.editorProps.disabled;
        },
        attributes: {
          'data-gramm': 'false'
        }
      };
    });
    _defineProperty(_this, "createEditorView", function (node) {
      measureRender(measurements.PROSEMIRROR_RENDERED, function (_ref3) {
        var duration = _ref3.duration,
          startTime = _ref3.startTime,
          distortedDuration = _ref3.distortedDuration;
        _this.proseMirrorRenderedSeverity = getAnalyticsEventSeverity(duration, PROSEMIRROR_RENDERED_NORMAL_SEVERITY_THRESHOLD, PROSEMIRROR_RENDERED_DEGRADED_SEVERITY_THRESHOLD);
        if (_this.view) {
          var _this$pluginInjection2;
          var nodes = getNodesCount(_this.view.state.doc);
          var ttfb = getResponseEndTime();
          var contextIdentifier = (_this$pluginInjection2 = _this.pluginInjectionAPI.api().base) === null || _this$pluginInjection2 === void 0 ? void 0 : _this$pluginInjection2.sharedState.currentState();
          _this.dispatchAnalyticsEvent({
            action: ACTION.PROSEMIRROR_RENDERED,
            actionSubject: ACTION_SUBJECT.EDITOR,
            attributes: {
              duration: duration,
              startTime: startTime,
              nodes: nodes,
              ttfb: ttfb,
              severity: _this.proseMirrorRenderedSeverity,
              objectId: contextIdentifier === null || contextIdentifier === void 0 ? void 0 : contextIdentifier.objectId,
              distortedDuration: distortedDuration
            },
            eventType: EVENT_TYPE.OPERATIONAL
          });
        }
      });

      // Creates the editor-view from this.editorState. If an editor has been mounted
      // previously, this will contain the previous state of the editor.
      _this.view = new EditorView({
        mount: node
      }, _this.getDirectEditorProps());
      _this.pluginInjectionAPI.onEditorViewUpdated({
        newEditorState: _this.view.state,
        oldEditorState: undefined
      });
    });
    _defineProperty(_this, "handleEditorViewRef", function (node) {
      if (!_this.view && node) {
        _this.createEditorView(node);
        // Ignored via go/ees005
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        var view = _this.view;
        _this.props.onEditorCreated({
          view: view,
          config: _this.config,
          eventDispatcher: _this.eventDispatcher,
          transformer: _this.contentTransformer
        });
        if (_this.props.editorProps.shouldFocus && view.props.editable && view.props.editable(view.state)) {
          _this.focusTimeoutId = handleEditorFocus(view);
        }

        // Force React to re-render so consumers get a reference to the editor view
        _this.forceUpdate();
      } else if (_this.view && !node) {
        // When the appearance is changed, React will call handleEditorViewRef with node === null
        // to destroy the old EditorView, before calling this method again with node === div to
        // create the new EditorView
        _this.props.onEditorDestroyed({
          view: _this.view,
          config: _this.config,
          eventDispatcher: _this.eventDispatcher,
          transformer: _this.contentTransformer
        });

        // Allows us to dispatch analytics within the plugin view.destory methods
        var analyticsConnected = _this.eventDispatcher.has(analyticsEventKey, _this.handleAnalyticsEvent);
        if (!analyticsConnected) {
          _this.eventDispatcher.on(analyticsEventKey, _this.handleAnalyticsEvent);
        }
        _this.view.destroy(); // Destroys the dom node & all node views

        if (!analyticsConnected) {
          _this.eventDispatcher.off(analyticsEventKey, _this.handleAnalyticsEvent);
        }
        _this.view = undefined;
      }
    });
    _defineProperty(_this, "dispatchAnalyticsEvent", function (payload) {
      if (_this.eventDispatcher) {
        var dispatch = createDispatch(_this.eventDispatcher);
        dispatch(analyticsEventKey, {
          payload: payload
        });
      }
    });
    _defineProperty(_this, "editorId", uuid());
    _defineProperty(_this, "createEditor", function (assistiveLabel, assistiveDescribedBy) {
      return /*#__PURE__*/React.createElement("div", {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
        className: getUAPrefix(),
        key: "ProseMirror",
        ref: _this.handleEditorViewRef,
        "aria-label": assistiveLabel || _this.props.intl.formatMessage(editorMessages.editorAssistiveLabel)
        // setting aria-multiline to true when not mobile appearance.
        //  because somehow mobile tests are failing when it set.
        //  don't know why that is happening.
        // Created https://product-fabric.atlassian.net/jira/servicedesk/projects/DTR/queues/issue/DTR-1675
        //  to investigate further.
        ,
        "aria-multiline": true,
        role: "textbox",
        id: EDIT_AREA_ID,
        "aria-describedby": assistiveDescribedBy,
        "data-editor-id": _this.editorId
      });
    });
    _defineProperty(_this, "editor", _this.createEditor(_this.props.editorProps.assistiveLabel, (_this$props$editorPro3 = _this.props.editorProps) === null || _this$props$editorPro3 === void 0 ? void 0 : _this$props$editorPro3.assistiveDescribedBy));
    _this.pluginInjectionAPI = new EditorPluginInjectionAPI({
      getEditorState: _this.getEditorState,
      getEditorView: _this.getEditorView,
      fireAnalyticsEvent: _this.handleAnalyticsEvent
    });
    var _api = _this.pluginInjectionAPI.api();
    (_props$setEditorAPI = props.setEditorAPI) === null || _props$setEditorAPI === void 0 || _props$setEditorAPI.call(props, _api);
    _this.eventDispatcher = new EventDispatcher();
    _this.dispatch = createDispatch(_this.eventDispatcher);
    _this.errorReporter = createErrorReporter(props.editorProps.errorReporterHandler);
    _this.pluginPerformanceObserver = new PluginPerformanceObserver(function (report) {
      return _this.onPluginObservation(report);
    }).withPlugins(function () {
      return _this.getPluginNames();
    }).withNodeCounts(function () {
      return _this.countNodes();
    });
    _this.featureFlags = createFeatureFlagsFromProps(_this.props.editorProps.featureFlags);
    var featureFlagsEnabled = _this.featureFlags ? getEnabledFeatureFlagKeys(_this.featureFlags) : [];

    // This needs to be before initialising editorState because
    // we dispatch analytics events in plugin initialisation
    _this.eventDispatcher.on(analyticsEventKey, _this.handleAnalyticsEvent);
    _this.eventDispatcher.on('resetEditorState', _this.resetEditorState);
    _this.editorState = _this.createEditorState({
      props: props,
      doc: props.editorProps.defaultValue,
      // ED-4759: Don't set selection at end for full-page editor - should be at start.
      selectionAtStart: isFullPage(props.editorProps.appearance)
    });
    _this.dispatchAnalyticsEvent({
      action: ACTION.STARTED,
      actionSubject: ACTION_SUBJECT.EDITOR,
      attributes: {
        platform: PLATFORMS.WEB,
        featureFlags: featureFlagsEnabled
      },
      eventType: EVENT_TYPE.UI
    });
    return _this;
  }
  _inherits(ReactEditorView, _React$Component);
  return _createClass(ReactEditorView, [{
    key: "transactionTracking",
    get:
    //TODO: clean up
    function get() {
      return {
        enabled: false
      };
    }
  }, {
    key: "getPluginNames",
    value: function getPluginNames() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return this.editorState.plugins.map(function (p) {
        return p.key;
      });
    }
  }, {
    key: "countNodes",
    value: function countNodes() {
      return _countNodes(this.editorState);
    }
  }, {
    key: "UNSAFE_componentWillReceiveProps",
    value:
    // Ignored via go/ees005
    // eslint-disable-next-line react/no-unsafe
    function UNSAFE_componentWillReceiveProps(nextProps) {
      var _nextProps$editorProp, _this$props$editorPro4;
      if (this.view && this.props.editorProps.disabled !== nextProps.editorProps.disabled) {
        // Disables the contentEditable attribute of the editor if the editor is disabled
        this.view.setProps({
          editable: function editable(_state) {
            return !nextProps.editorProps.disabled;
          }
        });
        if (!nextProps.editorProps.disabled && nextProps.editorProps.shouldFocus) {
          this.focusTimeoutId = handleEditorFocus(this.view);
        }
      }
      var appearance = this.props.editorProps.appearance;
      var nextAppearance = nextProps.editorProps.appearance;
      if (this.props.preset !== nextProps.preset) {
        this.reconfigureState(nextProps);
      }
      if (nextAppearance !== appearance) {
        if (nextAppearance === 'full-width' || appearance === 'full-width') {
          this.dispatchAnalyticsEvent({
            action: ACTION.CHANGED_FULL_WIDTH_MODE,
            actionSubject: ACTION_SUBJECT.EDITOR,
            eventType: EVENT_TYPE.TRACK,
            attributes: {
              previousMode: this.formatFullWidthAppearance(appearance),
              newMode: this.formatFullWidthAppearance(nextAppearance)
            }
          });
        }
      }
      if (nextProps.editorProps.assistiveLabel !== this.props.editorProps.assistiveLabel || ((_nextProps$editorProp = nextProps.editorProps) === null || _nextProps$editorProp === void 0 ? void 0 : _nextProps$editorProp.assistiveDescribedBy) !== ((_this$props$editorPro4 = this.props.editorProps) === null || _this$props$editorPro4 === void 0 ? void 0 : _this$props$editorPro4.assistiveDescribedBy)) {
        var _nextProps$editorProp2;
        this.editor = this.createEditor(nextProps.editorProps.assistiveLabel, (_nextProps$editorProp2 = nextProps.editorProps) === null || _nextProps$editorProp2 === void 0 ? void 0 : _nextProps$editorProp2.assistiveDescribedBy);
      }
    }
  }, {
    key: "reconfigureState",
    value: function reconfigureState(props) {
      var _this2 = this;
      if (!this.view) {
        return;
      }

      // We cannot currently guarentee when all the portals will have re-rendered during a reconfigure
      // so we blur here to stop ProseMirror from trying to apply selection to detached nodes or
      // nodes that haven't been re-rendered to the document yet.
      this.blur();
      var editorPlugins = this.getPlugins(props.preset);
      this.config = processPluginsList(editorPlugins);
      var state = this.editorState;
      var plugins = createPMPlugins({
        schema: state.schema,
        dispatch: this.dispatch,
        errorReporter: this.errorReporter,
        editorConfig: this.config,
        eventDispatcher: this.eventDispatcher,
        providerFactory: props.providerFactory,
        portalProviderAPI: props.portalProviderAPI,
        nodeViewPortalProviderAPI: props.nodeViewPortalProviderAPI,
        dispatchAnalyticsEvent: this.dispatchAnalyticsEvent,
        featureFlags: createFeatureFlagsFromProps(props.editorProps.featureFlags),
        getIntl: function getIntl() {
          return _this2.props.intl;
        },
        onEditorStateUpdated: fg('platform_editor_catch_missing_injection_states') ? this.pluginInjectionAPI.onEditorViewUpdated : undefined
      });
      var newState = state.reconfigure({
        plugins: plugins
      });

      // need to update the state first so when the view builds the nodeviews it is
      // using the latest plugins
      this.view.updateState(newState);
      return this.view.update(_objectSpread(_objectSpread({}, this.view.props), {}, {
        state: newState
      }));
    }
  }, {
    key: "componentDidMount",
    value: function componentDidMount() {
      // Transaction dispatching is already enabled by default prior to
      // mounting, but we reset it here, just in case the editor view
      // instance is ever recycled (mounted again after unmounting) with
      // the same key.
      // Although storing mounted state is an anti-pattern in React,
      // we do so here so that we can intercept and abort asynchronous
      // ProseMirror transactions when a dismount is imminent.
      this.canDispatchTransactions = true;
    }

    /**
     * Clean up any non-PM resources when the editor is unmounted
     */
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      // We can ignore any transactions from this point onwards.
      // This serves to avoid potential runtime exceptions which could arise
      // from an async dispatched transaction after it's unmounted.
      this.canDispatchTransactions = false;
      clearTimeout(this.focusTimeoutId);
      if (this.reliabilityInterval) {
        clearInterval(this.reliabilityInterval);
      }
      this.pluginPerformanceObserver.disconnect();
      if (this.view) {
        // Destroy the state if the Editor is being unmounted
        var editorState = this.view.state;
        editorState.plugins.forEach(function (plugin) {
          var state = plugin.getState(editorState);
          if (state && state.destroy) {
            state.destroy();
          }
        });
      }
      this.eventDispatcher.destroy();
      // this.view will be destroyed when React unmounts in handleEditorViewRef
    }
  }, {
    key: "getPlugins",
    value:
    // Helper to allow tests to inject plugins directly
    function getPlugins(preset) {
      var plugins = createPluginsList(preset, this.props.editorProps, this.pluginInjectionAPI);
      this.editorPlugins = plugins;
      return this.editorPlugins;
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props$render, _this$props$render2, _this$props;
      // Render tracking is firing too many events in Jira so we are disabling them for now. See - https://product-fabric.atlassian.net/browse/ED-25616
      var renderTrackingEnabled = !fg('platform_editor_disable_rerender_tracking_jira');
      var useShallow = true;
      return /*#__PURE__*/React.createElement(ReactEditorViewContext.Provider, {
        value: {
          editorRef: this.editorRef,
          editorView: this.view,
          popupsMountPoint: this.props.editorProps.popupsMountPoint
        }
      }, renderTrackingEnabled && /*#__PURE__*/React.createElement(RenderTracking, {
        componentProps: this.props,
        action: ACTION.RE_RENDERED,
        actionSubject: ACTION_SUBJECT.REACT_EDITOR_VIEW,
        handleAnalyticsEvent: this.handleAnalyticsEvent,
        useShallow: useShallow
      }), this.props.render ? (_this$props$render = (_this$props$render2 = (_this$props = this.props).render) === null || _this$props$render2 === void 0 ? void 0 : _this$props$render2.call(_this$props, {
        editor: this.editor,
        view: this.view,
        config: this.config,
        eventDispatcher: this.eventDispatcher,
        transformer: this.contentTransformer,
        dispatchAnalyticsEvent: this.dispatchAnalyticsEvent,
        editorRef: this.editorRef,
        editorAPI: this.props.editorAPI
      })) !== null && _this$props$render !== void 0 ? _this$props$render : this.editor : this.editor);
    }
  }]);
}(React.Component);
function getUAPrefix() {
  if (browser.chrome) {
    return 'ua-chrome';
  } else if (browser.ie) {
    return 'ua-ie';
  } else if (browser.gecko) {
    return 'ua-firefox';
  } else if (browser.safari) {
    return 'ua-safari';
  }
  return '';
}
export default injectIntl(ReactEditorView);